import React from "react";
import useSWR from "swr";
import { CheckSquare } from "react-feather";
import { useTranslation } from "next-i18next";
import { Flex, Box, Text, Button, Stack } from "@chakra-ui/react";

import BoxCard from "@/components/common/BoxCard";
import Avatar from "@/components/common/Avatar";
import Link from "@/components/common/Link";
import { Website } from "@/types/website";
import { format } from "date-fns";
import paths from "@/utils/paths";
import { getFileUri } from "@/utils/helpers";

type Props = {
  website: Website;
  rounded?: boolean;
};

const Accreditation: React.FC<Props> = ({ website, rounded }: Props) => {
  const { t } = useTranslation("common");
  const isOrganization = !!website?.organization;

  const { data: events } = useSWR(
    isOrganization
      ? `/event/organization/${website.organization.id}?active=true`
      : `/event/office/${website.pressOffice.id}?active=true`
  );

  return (
    <Stack spacing={3}>
      {events?.map((event) => (
        <BoxCard
          key={event.id}
          borderRadius={rounded ? ".375rem" : 0}
          p={0}
          overflowX={{ base: "auto", md: "unset" }}
        >
          <Flex align="center" flexWrap={{ base: "wrap", md: "nowrap" }}>
            <Avatar
              src={event.picture ? getFileUri(event.picture) : null}
              size="md"
              ml={{ base: "1.25rem", md: 4 }}
              name={event.name}
              my={{ base: ".75rem", md: 0 }}
            />
            <Box
              p=".75rem 1.25rem"
              w={{ base: "calc(100% - 68px)", md: "auto" }}
            >
              <Text as="h3" fontWeight="500">
                {event.name}
              </Text>
              <Text
                color="text.500"
                fontSize="xs"
                whiteSpace={{ base: "nowrap", md: "normal" }}
              >
                {format(new Date(event.startDate), "dd/MM/yyyy")} -{" "}
                {format(new Date(event.endDate), "dd/MM/yyyy")}
              </Text>
            </Box>
            {event.accreditationActive && (
              <Flex
                p=".75rem 1.25rem"
                ml="auto"
                justify="flex-end"
                align={{ base: "center", md: "flex-start" }}
                direction="column"
                w={{ base: "100%", md: "auto" }}
              >
                <Link
                  to={paths.getWebsiteHref(
                    `/accreditation/${event.id}`,
                    website?.slug,
                    website?.isSubdomain
                  )}
                >
                  <Button leftIcon={<CheckSquare />}>
                    {t("events.accredit")}
                  </Button>
                </Link>
                <Text color="text.500" fontSize="xs" mt={1} textAlign="right">
                  {t("events.accreditationTo", {
                    date: format(
                      new Date(event.accreditationTo),
                      "dd/MM/yyyy HH:mm"
                    ),
                  })}
                </Text>
              </Flex>
            )}
            {!event.accreditationActive &&
              event.accreditationFrom &&
              new Date() < new Date(event.accreditationFrom) && (
                <Text
                  color="text.500"
                  fontSize="xs"
                  m=".75rem 1.25rem"
                  ml="auto"
                >
                  {t("events.accreditationFrom", {
                    date: format(
                      new Date(event.accreditationFrom),
                      "dd/MM/yyyy HH:mm"
                    ),
                  })}
                </Text>
              )}
            {!event.accreditationActive && !event.accreditationFrom && (
              <Text color="text.500" fontSize="xs" m=".75rem 1.25rem" ml="auto">
                {t("events.accreditationNotStarted")}
              </Text>
            )}
            {!event.accreditationActive &&
              event.accreditationFrom &&
              new Date() > new Date(event.accreditationFrom) && (
                <Text
                  color="text.500"
                  fontSize="xs"
                  m=".75rem 1.25rem"
                  ml="auto"
                >
                  {t("events.accreditationEnded")}
                </Text>
              )}
          </Flex>
        </BoxCard>
      ))}
    </Stack>
  );
};

export default Accreditation;
